// src/products/dataroom/components/Modals/PermissionManager.js
import React, { useState, useEffect, useCallback } from 'react';
import { createPortal } from 'react-dom';
import { X, Users, Eye, Download, Lock, Printer, AlertTriangle } from 'lucide-react';
import { supabase } from '../../../../config';
import RoleService from '../../../../services/roleService';

const PermissionManager = ({ isOpen, onClose, itemId, projectId, itemName, itemType, userRole, onMessage }) => {
  const [teams, setTeams] = useState([]);
  const [permissions, setPermissions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);

  // FIXED: Use RoleService to check permissions
  const canManagePermissions = RoleService.hasAdminAccess(userRole?.toLowerCase());

  // FIXED: Updated Permission Types (View, Print, Download only)
  const permissionTypes = [
    { 
      id: 'view', 
      name: 'View', 
      icon: Eye, 
      color: 'blue',
      description: 'Can view file/folder structure only'
    },
    { 
      id: 'print', 
      name: 'Print', 
      icon: Printer, 
      color: 'purple',
      description: 'View + Print file content'
    },
    { 
      id: 'download', 
      name: 'Download', 
      icon: Download, 
      color: 'green',
      description: 'View + Print + Download file content'
    }
  ];

  // Fetch data function
  const fetchData = useCallback(async () => {
    if (!projectId || !itemId) return;
    
    setLoading(true);
    try {
      // Fetch teams for this project (across all parties)
      const { data: teamsData, error: teamsError } = await supabase
        .from('dataroom_teams')
        .select(`
          *,
          dataroom_parties!inner (
            id,
            name,
            project_id
          )
        `)
        .eq('dataroom_parties.project_id', projectId)
        .order('name');

      if (teamsError) throw teamsError;

      // Fetch existing permissions for this item (Teams-based)
      const { data: permissionsData, error: permissionsError } = await supabase
        .from('dataroom_file_permissions')
        .select('*')
        .eq('dataroom_item_id', itemId);

      if (permissionsError) throw permissionsError;

      setTeams(teamsData || []);
      setPermissions(permissionsData || []);
    } catch (error) {
      console.error('Error fetching data:', error);
      onMessage && onMessage('Failed to load permission data: ' + error.message, 'error');
    } finally {
      setLoading(false);
    }
  }, [projectId, itemId, onMessage]);

  useEffect(() => {
    if (isOpen) {
      fetchData();
    }
  }, [isOpen, fetchData]);

  // Get current permission for a team
  const getTeamPermission = (teamId) => {
    const permission = permissions.find(p => p.team_id === teamId);
    return permission?.permission_type || null;
  };

  // Set permission for a team
  const setTeamPermission = async (teamId, permissionType) => {
    if (!canManagePermissions) {
      onMessage && onMessage('Permission denied: Only Super Admin, Creator, or Support can manage permissions.', 'error');
      return;
    }

    setSaving(true);
    try {
      if (permissionType === null) {
        // Remove permission
        const { error } = await supabase
          .from('dataroom_file_permissions')
          .delete()
          .eq('team_id', teamId)
          .eq('dataroom_item_id', itemId);

        if (error) throw error;

        setPermissions(prev => prev.filter(p => !(p.team_id === teamId && p.dataroom_item_id === itemId)));
      } else {
        // Set/update permission
        const { error } = await supabase
          .from('dataroom_file_permissions')
          .upsert({
            team_id: teamId,
            dataroom_item_id: itemId,
            permission_type: permissionType
          });

        if (error) throw error;

        setPermissions(prev => {
          const existing = prev.find(p => p.team_id === teamId && p.dataroom_item_id === itemId);
          if (existing) {
            return prev.map(p => 
              p.team_id === teamId && p.dataroom_item_id === itemId 
                ? { ...p, permission_type: permissionType }
                : p
            );
          } else {
            return [...prev, { team_id: teamId, dataroom_item_id: itemId, permission_type: permissionType }];
          }
        });
      }

      onMessage && onMessage('Permission updated successfully!', 'success');
    } catch (error) {
      console.error('Error updating permission:', error);
      onMessage && onMessage('Failed to update permission: ' + error.message, 'error');
    } finally {
      setSaving(false);
    }
  };

  // Helper functions
  const getPermissionIcon = (permissionType) => {
    const permission = permissionTypes.find(p => p.id === permissionType);
    return permission?.icon || Lock;
  };

  const getPermissionColor = (permissionType) => {
    const permission = permissionTypes.find(p => p.id === permissionType);
    return permission?.color || 'gray';
  };

  const getPermissionName = (permissionType) => {
    const permission = permissionTypes.find(p => p.id === permissionType);
    return permission?.name || 'No Access';
  };

  // Early return if not open
  if (!isOpen) return null;

  // Access denied for viewers
  if (userRole === 'viewer') {
    return createPortal(
      <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
        <div className="bg-white rounded-xl shadow-2xl w-full max-w-md">
          <div className="flex justify-between items-center border-b p-6">
            <h3 className="text-xl font-semibold text-gray-800">Access Denied</h3>
            <button onClick={onClose} className="text-gray-400 hover:text-gray-600">
              <X size={24} />
            </button>
          </div>
          <div className="p-6 text-center">
            <AlertTriangle className="mx-auto h-12 w-12 text-red-400 mb-4" />
            <h4 className="text-lg font-medium text-gray-900 mb-2">Permission Required</h4>
            <p className="text-gray-500">You don't have permission to manage permissions for this item.</p>
            <p className="text-sm text-gray-400 mt-2">Contact a Creator or Support user for assistance.</p>
          </div>
        </div>
      </div>,
      document.body
    );
  }

  return createPortal(
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
      <div className="bg-white rounded-xl shadow-2xl w-full max-w-4xl max-h-[90vh] overflow-hidden">
        <div className="flex justify-between items-center border-b p-6">
          <div>
            <h3 className="text-xl font-semibold text-gray-800">Manage Permissions</h3>
            <p className="text-sm text-gray-600 mt-1">
              {itemName} ({itemType === 'folder' ? 'Folder' : 'File'})
            </p>
            {!canManagePermissions && (
              <p className="text-xs text-red-500 mt-1 flex items-center">
                <Lock className="h-3 w-3 mr-1" /> View Only - Contact Creator/Support to edit permissions.
              </p>
            )}
          </div>
          <button 
            onClick={onClose} 
            className="text-gray-400 hover:text-gray-600"
            disabled={saving}
          >
            <X size={24} />
          </button>
        </div>

        <div className="p-6 overflow-y-auto max-h-[calc(90vh-120px)]">
          {loading ? (
            <div className="flex items-center justify-center h-32">
              <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
            </div>
          ) : (
            <div className="space-y-6">
              {/* Permission Types Reference */}
              <div className="bg-gray-50 rounded-lg p-4">
                <h4 className="font-medium text-gray-900 mb-3">Permission Types</h4>
                <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
                  {permissionTypes.map((permission) => (
                    <div key={permission.id} className="flex items-start space-x-2 p-2 bg-white rounded border">
                      <permission.icon className={`h-4 w-4 text-${permission.color}-600 mt-0.5 flex-shrink-0`} />
                      <div>
                        <span className="text-sm font-medium text-gray-900">{permission.name}</span>
                        <p className="text-xs text-gray-600">{permission.description}</p>
                      </div>
                    </div>
                  ))}
                </div>
              </div>

              {/* Teams and Permissions */}
              {teams.length === 0 ? (
                <div className="text-center py-8">
                  <Users className="mx-auto h-12 w-12 text-gray-400 mb-4" />
                  <h3 className="text-lg font-medium text-gray-900 mb-2">No teams found</h3>
                  <p className="text-gray-500">Create parties and teams first to manage permissions</p>
                </div>
              ) : (
                <div className="space-y-4">
                  <h4 className="font-medium text-gray-900">Team Permissions</h4>
                  {teams.map((team) => {
                    const currentPermission = getTeamPermission(team.id);
                    const PermissionIcon = getPermissionIcon(currentPermission);
                    const party = team.dataroom_parties;
                    
                    return (
                      <div key={team.id} className="border border-gray-200 rounded-lg p-4">
                        <div className="flex items-center justify-between mb-4">
                          <div className="flex items-center space-x-3">
                            <div className="w-10 h-10 bg-blue-100 rounded-full flex items-center justify-center">
                              <Users className="h-5 w-5 text-blue-600" />
                            </div>
                            <div>
                              <h5 className="font-medium text-gray-900">{team.name}</h5>
                              {party && (
                                <p className="text-sm text-gray-500">
                                  Party: {party.name}
                                </p>
                              )}
                              {team.description && (
                                <p className="text-xs text-gray-600 mt-1">{team.description}</p>
                              )}
                            </div>
                          </div>
                          <div className="flex items-center space-x-2">
                            <PermissionIcon className={`h-5 w-5 text-${getPermissionColor(currentPermission)}-600`} />
                            <span className={`text-sm font-medium text-${getPermissionColor(currentPermission)}-600`}>
                              {getPermissionName(currentPermission)}
                            </span>
                          </div>
                        </div>

                        {/* Permission Buttons */}
                        {canManagePermissions ? (
                          <div className="grid grid-cols-2 md:grid-cols-4 gap-2">
                            {/* No Access Option */}
                            <button
                              onClick={() => setTeamPermission(team.id, null)}
                              disabled={saving}
                              className={`p-2 rounded-md text-sm font-medium transition-colors flex items-center justify-center space-x-1 ${
                                currentPermission === null
                                  ? 'bg-gray-200 text-gray-900 border-2 border-gray-400'
                                  : 'bg-gray-50 text-gray-600 hover:bg-gray-100 border border-gray-200'
                              } ${saving ? 'opacity-50 cursor-not-allowed' : ''}`}
                            >
                              <Lock className="h-4 w-4" />
                              <span>No Access</span>
                            </button>

                            {/* Permission Options */}
                            {permissionTypes.map((permission) => (
                              <button
                                key={permission.id}
                                onClick={() => setTeamPermission(team.id, permission.id)}
                                disabled={saving}
                                className={`p-2 rounded-md text-sm font-medium transition-colors flex items-center justify-center space-x-1 ${
                                  currentPermission === permission.id
                                    ? `bg-${permission.color}-100 text-${permission.color}-900 border-2 border-${permission.color}-400`
                                    : `bg-${permission.color}-50 text-${permission.color}-600 hover:bg-${permission.color}-100 border border-${permission.color}-200`
                                } ${saving ? 'opacity-50 cursor-not-allowed' : ''}`}
                              >
                                <permission.icon className="h-4 w-4" />
                                <span>{permission.name}</span>
                              </button>
                            ))}
                          </div>
                        ) : (
                          <div className="text-center py-4 bg-gray-50 rounded-lg">
                            <Lock className="mx-auto h-6 w-6 text-gray-400 mb-2" />
                            <p className="text-sm text-gray-500">Permission management restricted</p>
                          </div>
                        )}
                      </div>
                    );
                  })}
                </div>
              )}
            </div>
          )}
        </div>

        {/* Footer */}
        <div className="flex justify-between items-center border-t p-6 bg-gray-50">
          <div className="text-sm text-gray-500">
            {saving && (
              <div className="flex items-center">
                <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-blue-600 mr-2"></div>
                Saving changes...
              </div>
            )}
          </div>
          <button
            onClick={onClose}
            disabled={saving}
            className="px-4 py-2 bg-gray-200 text-gray-800 rounded-lg hover:bg-gray-300 disabled:opacity-50"
          >
            {saving ? 'Saving...' : 'Close'}
          </button>
        </div>
      </div>
    </div>,
    document.body
  );
};

export default PermissionManager;